import PropTypes from 'prop-types';
import { use, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { getContentType } from '../../lib/flotiq-client';
import { getCtdsFromQuery } from '../../lib/flotiq-client/api-helpers';
import {
  ResponseError,
  checkResponseStatus,
} from '../../lib/flotiq-client/response-errors';
import { EMPTY_ARRAY } from '../../lib/globals';

import { useContentTypes } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { ModalInstanceContext, useModals } from '../../contexts/ModalContext';
import PropertyModalCtdsContext from '../../contexts/PropertyModalCtdsContext';

import PropertyModalFormContent from './PropertyModalFormContent/PropertyModalFormContent';
import { generateZodSchema } from './helpers/validators';

const CTD_PARAMS = {
  limit: 20,
  order_by: 'label',
  order_direction: 'asc',
};

const PropertyModalForm = ({
  property,
  order = EMPTY_ARRAY,
  isNew = false,
  isDuplicate = false,
  testId = '',
  parentInputType,
}) => {
  const jwt = useToken();
  const { space } = useSelectedSpace();
  const { t } = useTranslation();
  const modal = useModals();
  const modalInstance = use(ModalInstanceContext);

  const {
    data: ctds,
    errors: ctdsErrors,
    isLoading: ctdsAreLoading,
  } = useContentTypes(CTD_PARAMS);

  const finalCtdOptions = useMemo(() => {
    const options = ctds
      .filter((ctd) => !ctd.internal || ['_media', '_tag'].includes(ctd.name))
      .map((ctd) => ({
        label: ctd.label,
        value: ctd.name,
      }));
    options.unshift({
      label: t('ContentTypeForm.Descriptions.AnyType'),
      value: '',
    });
    return options;
  }, [ctds, t]);

  useApiErrorsToast(ctdsErrors);

  const getValidationType = useCallback(
    async (contentTypeName) => {
      try {
        const { body, status } = await getContentType(jwt, space, {
          contentTypeName,
        });
        checkResponseStatus(body, status);
        return body;
      } catch (error) {
        toast.error(
          t(
            error instanceof ResponseError
              ? 'ContentForm.CouldntFetch'
              : 'Form.CommunicationErrorMessage',
          ),
        );
        return null;
      }
    },
    [jwt, space, t],
  );

  const filterCtds = useCallback(
    async (query, _, setIsLoading) => {
      setIsLoading(true);

      const newCtds = (
        await getCtdsFromQuery(jwt, space, { ...CTD_PARAMS, label: query }, t)
      )
        .filter((ctd) => !ctd.internal || ['_media', '_tag'].includes(ctd.name))
        .map((ctd) => ({ label: ctd.label, value: ctd.name }));

      setIsLoading(false);

      if (
        t('ContentTypeForm.Descriptions.AnyType')
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.toLowerCase().replace(/\s+/g, ''))
      ) {
        newCtds.unshift({
          label: t('ContentTypeForm.Descriptions.AnyType'),
          value: '',
        });
      }
      return newCtds;
    },
    [jwt, space, t],
  );

  const propertyCtdsContextValue = useMemo(
    () => ({
      ctds: finalCtdOptions,
      getValidationType,
      ctdsAreLoading,
      filterCtds,
    }),
    [finalCtdOptions, ctdsAreLoading, filterCtds, getValidationType],
  );

  const handleSubmit = useCallback(
    async ({ value }) => {
      if (property && property.key !== value.key && !isNew) {
        const result = await modal.confirmation(
          t('PropertyForm.ChangeKeyConfirm'),
        );
        if (!result) return;
      }

      modalInstance.resolve(value);
    },
    [property, isNew, modalInstance, modal, t],
  );

  const validationSchema = useMemo(
    () => generateZodSchema(t, isDuplicate, property, order),
    [t, property, order, isDuplicate],
  );

  return (
    <PropertyModalCtdsContext value={propertyCtdsContextValue}>
      <PropertyModalFormContent
        onSubmit={handleSubmit}
        property={property}
        validationSchema={validationSchema}
        parentInputType={parentInputType}
        testId={testId}
      />
    </PropertyModalCtdsContext>
  );
};

export default PropertyModalForm;

PropertyModalForm.propTypes = {
  /**
   * Property object to edit
   */
  property: PropTypes.object,
  /**
   * Order of current content type
   */
  order: PropTypes.array,
  /**
   * If property is new
   */
  isNew: PropTypes.bool,
  /**
   * If property is duplication
   */
  isDuplicate: PropTypes.bool,
  /**
   * Test id for property modal form
   */
  testId: PropTypes.string,
};
